import { parse } from 'tldts';

import { config } from '../config';
import { api } from '../utils/apiUtils';
import { createRequestHeaders } from '../utils/domainUtils';

const BASE_URL = `${config.API_URI}/v1/white-label`;


/**
 * Helper function to get headers with white label domain detection
 * @deprecated Use createRequestHeaders from domainUtils instead
 */
const getWhiteLabelHeaders = () => {
  return createRequestHeaders();
};

/**
 * White Label API Service
 * Handles all white label related API calls
 */
class WhiteLabelService {

  /**
   * Get white label configuration for current user
   */
  async getConfig() {
    try {
      const response = await api.get(`${BASE_URL}/config`);
      return response;
    } catch (error) {
      console.error('Error fetching white label config:', error);

      // If it's a 404, the user doesn't have white label yet - this is expected
      if (error.status === 404 || error.message?.includes('No white label configuration found')) {
        return { hasWhiteLabel: false };
      }

      // If it's a 403 with addon required message, user needs to purchase
      if (error.status === 403 && error.message?.includes('White label addon required')) {
        return { hasWhiteLabel: false, needsPurchase: true };
      }

      throw error;
    }
  }

  /**
   * Create a new white label instance
   */
  async create(data) {
    try {
      const response = await api.post(`${BASE_URL}/create`, data);
      return response;
    } catch (error) {
      console.error('Error creating white label:', error);
      throw error;
    }
  }

  /**
   * Update white label configuration
   */
  async updateConfig(data) {
    try {
      const response = await api.put(`${BASE_URL}/config`, data);
      return response;
    } catch (error) {
      console.error('Error updating white label config:', error);
      throw error;
    }
  }

  /**
   * Upload logo for white label
   */
  async uploadLogo(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      // Add upload options
      if (options.uploadOriginal) {
        formData.append('uploadOriginal', 'true');
      }

      if (options.cropData) {
        formData.append('cropData', JSON.stringify(options.cropData));
      }

      const response = await fetch(`${BASE_URL}/logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload logo');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  }

  /**
   * Upload dark theme logo for white label
   */
  async uploadLogoDark(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('logoDark', file);

      // Add upload options
      if (options.uploadOriginal) {
        formData.append('uploadOriginal', 'true');
      }

      if (options.cropData) {
        formData.append('cropData', JSON.stringify(options.cropData));
      }

      const response = await fetch(`${BASE_URL}/logo-dark`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload dark logo');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading dark logo:', error);
      throw error;
    }
  }

  /**
   * Upload logo icon (square) for white label
   */
  async uploadLogoIcon(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('logoIcon', file);

      if (options.size) {
        formData.append('size', String(options.size));
      }
      if (options.cropData) {
        formData.append('cropData', JSON.stringify(options.cropData));
      }

      const response = await fetch(`${BASE_URL}/logo-icon`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload logo icon');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading logo icon:', error);
      throw error;
    }
  }

  /**
   * Upload favicon for white label
   */
  async uploadFavicon(file) {
    try {
      const formData = new FormData();
      formData.append('favicon', file);

      const response = await fetch(`${BASE_URL}/favicon`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload favicon');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading favicon:', error);
      throw error;
    }
  }

  /**
   * Set custom domain
   */
  async setCustomDomain(domain) {
    try {
      const response = await api.post(`${BASE_URL}/domain`, { customDomain: domain });
      return response;
    } catch (error) {
      console.error('Error setting custom domain:', error);
      throw error;
    }
  }

  /**
   * Verify custom domain
   */
  async verifyDomain() {
    try {
      const response = await api.post(`${BASE_URL}/domain/verify`);
      return response;
    } catch (error) {
      console.error('Error verifying domain:', error);
      throw error;
    }
  }

  /**
   * Get public branding (no auth required)
   */
  async getPublicBranding() {
    try {
      const response = await fetch(`${BASE_URL}/branding`, {
        method: 'GET',
        credentials: 'include',
        headers: getWhiteLabelHeaders(),
      });

      if (response.status === 403) {
        // Handle unauthorized domain access
        try {
          const errorData = await response.json();
          if (errorData.redirectTo) {
            console.log('Unauthorized domain detected in service, redirecting to:', errorData.redirectTo);
            window.location.href = errorData.redirectTo;
            return; // This won't execute due to redirect
          }
        } catch (e) {
          console.error('Error parsing 403 response in service:', e);
        }
        throw new Error('Domain not authorized');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch branding');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching public branding:', error);
      throw error;
    }
  }

  /**
   * Update branding configuration
   */
  async updateBranding(branding) {
    try {
      const response = await api.put(`${BASE_URL}/config`, { branding });
      return response;
    } catch (error) {
      console.error('Error updating branding:', error);
      throw error;
    }
  }

  /**
   * Update features configuration
   */
  async updateFeatures(features) {
    try {
      const response = await api.put(`${BASE_URL}/config`, { features });
      return response;
    } catch (error) {
      console.error('Error updating features:', error);
      throw error;
    }
  }

  /**
   * Check if user can create white label
   */
  async canCreateWhiteLabel() {
    try {
      const config = await this.getConfig();
      return !config.hasWhiteLabel;
    } catch (error) {
      // If error fetching config, assume they can create
      return true;
    }
  }

  /**
   * Get white label payment status
   */
  async getPaymentStatus() {
    try {
      const response = await fetch(`${config.API_URI}/v1/stripe/white-label-status`, {
        method: 'GET',
        headers: getWhiteLabelHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting white label payment status:', error);
      return {
        hasAddon: false,
        isOwner: false,
        canCreateInstance: false,
        instance: null
      };
    }
  }

  /**
   * Get white label status and billing info
   */
  async getStatus() {
    try {
      const config = await this.getConfig();

      if (!config.hasWhiteLabel) {
        return {
          hasWhiteLabel: false,
          status: 'none',
          addonActive: false
        };
      }

      return {
        hasWhiteLabel: true,
        status: config.config.status,
        addonActive: config.config.addonActive,
        domain: config.config.domain,
        branding: config.config.branding,
        features: config.config.features
      };
    } catch (error) {
      console.error('Error getting white label status:', error);
      return {
        hasWhiteLabel: false,
        status: 'error',
        addonActive: false,
        error: error.message
      };
    }
  }

  /**
   * Cancel white label addon subscription
   */
  async cancelSubscription() {
    try {
      const response = await api.post(`${config.API_URI}/v1/stripe/cancel-white-label-addon`);
      return response;
    } catch (error) {
      console.error('Error canceling white label subscription:', error);
      throw error;
    }
  }

  /**
   * Start white label trial (90 days)
   */
  async startTrial() {
    try {
      const response = await api.post(`${BASE_URL}/start-trial`);
      return response;
    } catch (error) {
      console.error('Error starting white label trial:', error);
      throw error;
    }
  }

  /**
   * Validate domain format
   */

  validateDomain(domain) {
    if (!domain) {
      return { valid: false, error: 'Domain is required' };
    }

    if (domain.includes('zoobbe.com')) {
      return { valid: false, error: 'Cannot use zoobbe.com domains' };
    }

    // Basic format check (e.g., prevent IPs or gibberish)
    const hostnameRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    if (!hostnameRegex.test(domain)) {
      return { valid: false, error: 'Invalid domain format' };
    }

    const result = parse(domain);

    if (!result.domain || !result.publicSuffix || !result.isIcann) {
      return { valid: false, error: 'Invalid or unsupported domain' };
    }

    return { valid: true };
  }

  /**
   * Validate app name
   */
  validateAppName(appName) {
    if (!appName) {
      return { valid: false, error: 'App name is required' };
    }

    if (appName.length < 2 || appName.length > 30) {
      return { valid: false, error: 'App name must be between 2 and 30 characters' };
    }

    return { valid: true };
  }

  /**
   * Validate color format
   */
  validateColor(color) {
    if (!color) {
      return { valid: true }; // Optional field
    }

    const colorRegex = /^#[0-9A-F]{6}$/i;

    if (!colorRegex.test(color)) {
      return { valid: false, error: 'Color must be a valid hex color (e.g., #FF0000)' };
    }

    return { valid: true };
  }

  /**
   * Validate email format
   */
  validateEmail(email) {
    if (!email) {
      return { valid: true }; // Optional field
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Invalid email format' };
    }

    return { valid: true };
  }

  /**
   * Create payment intent for white label addon
   */
  async createPaymentIntent(billingCycle = 'yearly') {
    try {
      // Define price IDs for different billing cycles
      const priceIds = {
        monthly: 'price_1RozgaK0cSs6yAeTiZWxveYn',
        yearly: 'price_1RozgaK0cSs6yAeTace81o45'
      };

      const priceId = priceIds[billingCycle];
      if (!priceId) {
        throw new Error(`Invalid billing cycle: ${billingCycle}`);
      }

      const response = await fetch(`${config.API_URI}/v1/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'white_label_addon',
          priceId: priceId,
          billingCycle: billingCycle
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create payment intent');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating white label payment intent:', error);
      throw error;
    }
  }

  /**
   * Validate subdomain availability
   */
  async validateSubdomain(subdomain) {
    try {
      const response = await fetch(`${BASE_URL}/subdomain/validate?subdomain=${encodeURIComponent(subdomain)}`, {
        method: 'GET',
        headers: getWhiteLabelHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating subdomain:', error);
      throw error;
    }
  }

  /**
   * Get pricing information for white label addon
   */
  getPricingInfo() {
    const monthlyPrice = 9.99;
    const yearlyPrice = 95.99;
    const monthlyCost = monthlyPrice * 12; // $119.88
    const savings = Math.round(((monthlyCost - yearlyPrice) / monthlyCost) * 100); // 20%

    return {
      monthly: {
        amount: monthlyPrice,
        priceId: 'price_1RozgaK0cSs6yAeTiZWxveYn',
        period: 'month',
        savings: null,
        monthlyEquivalent: monthlyPrice
      },
      yearly: {
        amount: yearlyPrice,
        priceId: 'price_1RozgaK0cSs6yAeTace81o45',
        period: 'year',
        savings: `${savings}%`,
        monthlyEquivalent: Math.round((yearlyPrice / 12) * 100) / 100 // $8.00/month
      }
    };
  }

  /**
   * Get SMTP configuration
   */
  async getSmtpConfig() {
    try {
      const response = await api.get(`${BASE_URL}/smtp`);
      return response;
    } catch (error) {
      console.error('Error fetching SMTP config:', error);
      throw error;
    }
  }

  /**
   * Update SMTP configuration
   */
  async updateSmtpConfig(smtpData) {
    try {
      const response = await api.put(`${BASE_URL}/smtp`, smtpData);
      return response;
    } catch (error) {
      console.error('Error updating SMTP config:', error);
      throw error;
    }
  }

  /**
   * Test SMTP configuration
   */
  async testSmtpConfig(testEmail = null) {
    try {
      const response = await api.post(`${BASE_URL}/smtp/test`, { testEmail });
      return response;
    } catch (error) {
      console.error('Error testing SMTP config:', error);
      throw error;
    }
  }

  /**
   * Disable SMTP configuration
   */
  async disableSmtpConfig() {
    try {
      const response = await api.post(`${BASE_URL}/smtp/disable`);
      return response;
    } catch (error) {
      console.error('Error disabling SMTP config:', error);
      throw error;
    }
  }

  /**
   * Delete SMTP configuration
   */
  async deleteSmtpConfig() {
    try {
      const response = await api.delete(`${BASE_URL}/smtp`);
      return response;
    } catch (error) {
      console.error('Error deleting SMTP config:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const whiteLabelService = new WhiteLabelService();
export default whiteLabelService;
